import * as THREE from 'three'

export interface CarriedFlight {
  enter: (from: THREE.Vector3, heading: number) => void
  exit: () => void
  update: (dt: number, camera: THREE.PerspectiveCamera) => void
  setInput: (input: {
    forward: number
    turn: number
    climb: number
    boost: boolean
  }) => void
  position: THREE.Vector3
  heading: number
  dispose: () => void
}

const CRUISE_SPEED = 8
const BOOST_MULT = 1.8
const TURN_RATE = 0.9
const TARGET_CLEARANCE = 2.5
const MIN_CLEARANCE = 1.2
const LOOKAHEAD = 4
const LOOKAHEAD_STEPS = [1.2, 2.4, 4, 6, 8, 11] as const
const ALT_SPRING = 8.5
const ALT_DAMP = 9.5
const MAX_BANK = 0.35
const BANK_RESPONSIVENESS = 4.2
const CLIMB_RATE = 3.2
const CLIMB_BIAS_MIN = -1.2
const CLIMB_BIAS_MAX = 10
const CAM_BACK = 1.65
const CAM_BELOW = 0.5
const CAM_FOLLOW = 7
const LOOK_AHEAD_CAM = 5
const LOOK_DOWN = 0.55
const BOB_AMP = 0.035
const BOB_FREQ = 7.5
const WING_AMP = 0.55
const WING_FREQ = 9.5
const BOUND_MARGIN = 14
const BOUND_STEER = 1.35

function wrapAngle(angle: number): number {
  let a = angle
  while (a > Math.PI) a -= Math.PI * 2
  while (a < -Math.PI) a += Math.PI * 2
  return a
}

function shortestDelta(from: number, to: number): number {
  return wrapAngle(to - from)
}

function makeGuideBird(): {
  group: THREE.Group
  leftWing: THREE.Object3D
  rightWing: THREE.Object3D
  dispose: () => void
} {
  const group = new THREE.Group()
  group.name = 'carried-guide-bird'

  const black = new THREE.MeshStandardMaterial({ color: 0x17251e, roughness: 0.75 })
  const yellow = new THREE.MeshStandardMaterial({
    color: 0xf3c94f,
    emissive: 0x805514,
    emissiveIntensity: 0.18,
    roughness: 0.65,
  })
  const orange = new THREE.MeshStandardMaterial({ color: 0xf18435, roughness: 0.6 })
  const white = new THREE.MeshBasicMaterial({ color: 0xfff8dc })

  const body = new THREE.Mesh(new THREE.SphereGeometry(0.28, 12, 9), black)
  body.scale.set(0.85, 1.05, 1.35)
  const chest = new THREE.Mesh(new THREE.SphereGeometry(0.18, 10, 8), yellow)
  chest.position.set(0, -0.02, 0.22)
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), black)
  head.position.set(0, 0.12, 0.32)
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.42, 7), orange)
  beak.rotation.x = Math.PI / 2
  beak.position.set(0, 0.1, 0.58)
  const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.03, 6, 5), white)
  eyeL.position.set(0.1, 0.16, 0.4)
  const eyeR = eyeL.clone()
  eyeR.position.x = -0.1

  const leftWing = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.06, 0.28), black)
  leftWing.position.set(0.42, 0.04, 0)
  leftWing.geometry.translate(0.36, 0, 0)
  const rightWing = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.06, 0.28), black)
  rightWing.position.set(-0.42, 0.04, 0)
  rightWing.geometry.translate(-0.36, 0, 0)

  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.36, 6), black)
  tail.rotation.x = -Math.PI / 2
  tail.position.set(0, 0.02, -0.42)

  group.add(body, chest, head, beak, eyeL, eyeR, leftWing, rightWing, tail)
  group.scale.setScalar(0.85)

  const materials = [black, yellow, orange, white]
  const geometries = [
    body.geometry,
    chest.geometry,
    head.geometry,
    beak.geometry,
    eyeL.geometry,
    leftWing.geometry,
    rightWing.geometry,
    tail.geometry,
  ]

  return {
    group,
    leftWing,
    rightWing,
    dispose: () => {
      for (const geometry of geometries) geometry.dispose()
      for (const material of materials) material.dispose()
    },
  }
}

export function createCarriedFlight(input: {
  getHeight: (x: number, z: number) => number
  scene: THREE.Scene
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number }
}): CarriedFlight {
  const { getHeight, scene, bounds } = input
  const bird = makeGuideBird()
  bird.group.visible = false
  scene.add(bird.group)

  const position = new THREE.Vector3()
  const velocity = new THREE.Vector3()
  const cameraPos = new THREE.Vector3()
  const lookTarget = new THREE.Vector3()
  const forward = new THREE.Vector3()
  const desiredCam = new THREE.Vector3()
  const euler = new THREE.Euler(0, 0, 0, 'YXZ')
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

  let heading = 0
  let bank = 0
  let climbBias = 0
  let active = false
  let elapsed = 0
  let camInitialized = false
  let forwardInput = 1
  let turnInput = 0
  let climbInput = 0
  let boost = false

  const sampleTerrainAhead = (
    x: number,
    z: number,
    yaw: number,
    speed: number,
  ): number => {
    let peak = getHeight(x, z)
    const sin = Math.sin(yaw)
    const cos = Math.cos(yaw)
    // Stretch samples with speed so boost still clears the cone before contact.
    const stretch = Math.max(1, speed / CRUISE_SPEED)
    for (const dist of LOOKAHEAD_STEPS) {
      const d = dist * stretch
      peak = Math.max(peak, getHeight(x + sin * d, z - cos * d))
    }
    // Lateral samples catch ridgelines approached at a shallow angle.
    const side = 1.8
    const ahead = LOOKAHEAD * 0.7 * stretch
    peak = Math.max(
      peak,
      getHeight(x + cos * side + sin * ahead, z + sin * side - cos * ahead),
      getHeight(x - cos * side + sin * ahead, z - sin * side - cos * ahead),
    )
    return peak
  }

  const steerFromBounds = (dt: number) => {
    let steer = 0
    const { minX, maxX, minZ, maxZ } = bounds
    if (position.x < minX + BOUND_MARGIN) {
      const t = (minX + BOUND_MARGIN - position.x) / BOUND_MARGIN
      steer += shortestDelta(heading, Math.PI / 2) * t * BOUND_STEER
    } else if (position.x > maxX - BOUND_MARGIN) {
      const t = (position.x - (maxX - BOUND_MARGIN)) / BOUND_MARGIN
      steer += shortestDelta(heading, -Math.PI / 2) * t * BOUND_STEER
    }
    if (position.z < minZ + BOUND_MARGIN) {
      const t = (minZ + BOUND_MARGIN - position.z) / BOUND_MARGIN
      steer += shortestDelta(heading, Math.PI) * t * BOUND_STEER
    } else if (position.z > maxZ - BOUND_MARGIN) {
      const t = (position.z - (maxZ - BOUND_MARGIN)) / BOUND_MARGIN
      steer += shortestDelta(heading, 0) * t * BOUND_STEER
    }
    heading = wrapAngle(heading + steer * dt)
    position.x = THREE.MathUtils.clamp(position.x, minX + 0.5, maxX - 0.5)
    position.z = THREE.MathUtils.clamp(position.z, minZ + 0.5, maxZ - 0.5)
  }

  const enter = (from: THREE.Vector3, initialHeading: number) => {
    heading = wrapAngle(initialHeading)
    const ground = getHeight(from.x, from.z)
    position.set(from.x, Math.max(from.y, ground + TARGET_CLEARANCE), from.z)
    velocity.set(0, 0, 0)
    climbBias = 0
    bank = 0
    elapsed = 0
    camInitialized = false
    forwardInput = 1
    turnInput = 0
    climbInput = 0
    boost = false
    bird.group.visible = true
    active = true
  }

  const exit = () => {
    active = false
    bird.group.visible = false
  }

  const setInput = (next: {
    forward: number
    turn: number
    climb: number
    boost: boolean
  }) => {
    forwardInput = next.forward
    turnInput = THREE.MathUtils.clamp(next.turn, -1, 1)
    climbInput = THREE.MathUtils.clamp(next.climb, -1, 1)
    boost = next.boost
  }

  const update = (dt: number, camera: THREE.PerspectiveCamera) => {
    if (!active) return
    const reduce = reducedMotion.matches
    const bobAmp = reduce ? BOB_AMP * 0.12 : BOB_AMP
    const maxBank = reduce ? MAX_BANK * 0.22 : MAX_BANK
    const wingAmp = reduce ? WING_AMP * 0.2 : WING_AMP

    elapsed += dt
    const turnEased = turnInput * turnInput * Math.sign(turnInput)
    heading = wrapAngle(heading + turnEased * TURN_RATE * dt)
    steerFromBounds(dt)

    const speed =
      CRUISE_SPEED * (boost ? BOOST_MULT : 1) * THREE.MathUtils.clamp(forwardInput, 0.35, 1.25)
    forward.set(Math.sin(heading), 0, -Math.cos(heading))

    climbBias = THREE.MathUtils.clamp(
      climbBias + climbInput * CLIMB_RATE * dt,
      CLIMB_BIAS_MIN,
      CLIMB_BIAS_MAX,
    )
    if (Math.abs(climbInput) < 0.05) {
      climbBias = THREE.MathUtils.damp(climbBias, 0, 0.7, dt)
    }

    const terrain = sampleTerrainAhead(position.x, position.z, heading, speed)
    const floorY = getHeight(position.x, position.z) + MIN_CLEARANCE
    const targetY = Math.max(terrain + TARGET_CLEARANCE + climbBias, floorY)

    // Critically-ish damped spring toward clearance altitude.
    const accel = (targetY - position.y) * ALT_SPRING - velocity.y * ALT_DAMP
    velocity.y += accel * dt
    position.y += velocity.y * dt
    if (position.y < floorY) {
      position.y = floorY
      velocity.y = Math.max(0, velocity.y)
    }

    // Move after altitude solve so look-ahead reacts to the slope before contact.
    position.x += forward.x * speed * dt
    position.z += forward.z * speed * dt
    const postFloor = getHeight(position.x, position.z) + MIN_CLEARANCE
    if (position.y < postFloor) {
      position.y = postFloor
      velocity.y = Math.max(0, velocity.y)
    }

    const targetBank = -turnEased * maxBank
    bank += (targetBank - bank) * (1 - Math.exp(-BANK_RESPONSIVENESS * dt))

    const bob = Math.sin(elapsed * BOB_FREQ) * bobAmp
    const wingBeat = Math.sin(elapsed * WING_FREQ) * wingAmp
    bird.leftWing.rotation.z = wingBeat
    bird.rightWing.rotation.z = -wingBeat
    bird.group.position.set(position.x, position.y + bob * 0.4, position.z)
    bird.group.rotation.set(0.12 + climbInput * 0.08, heading, bank * 0.65)

    desiredCam.copy(position).addScaledVector(forward, -CAM_BACK)
    desiredCam.y += -CAM_BELOW + bob
    if (!camInitialized) {
      cameraPos.copy(desiredCam)
      camInitialized = true
    } else {
      const alpha = 1 - Math.exp(-CAM_FOLLOW * dt)
      cameraPos.lerp(desiredCam, alpha)
    }

    const camFloor = getHeight(cameraPos.x, cameraPos.z) + MIN_CLEARANCE * 0.85
    if (cameraPos.y < camFloor) cameraPos.y = camFloor

    lookTarget.copy(position).addScaledVector(forward, LOOK_AHEAD_CAM)
    lookTarget.y -= LOOK_DOWN

    camera.position.copy(cameraPos)
    camera.up.set(0, 1, 0)
    camera.lookAt(lookTarget)
    euler.setFromQuaternion(camera.quaternion)
    euler.z = bank
    camera.quaternion.setFromEuler(euler)
  }

  const dispose = () => {
    exit()
    scene.remove(bird.group)
    bird.dispose()
  }

  return {
    enter,
    exit,
    update,
    setInput,
    get position() {
      return position
    },
    get heading() {
      return heading
    },
    dispose,
  }
}
